import type { DrillProgress } from "../handwriting/xiezi-stages.js";
import type { DrillSounds } from "./xiezi-sounds.js";
import type { DrillParts } from "./xiezi-view.js";
import { passMoment } from "../handwriting/xiezi-moments.js";

/**
 * How a writing drill answers each move, besides the line under the square.
 *
 * A stroke taken plays a note. A stroke missed plays another and is written
 * in for the reader to see. A character written whole flashes green, plays
 * the note for what the pass finished, and is said aloud.
 * `src/handwriting/xiezi-moments.ts` has the notes, `xiezi-sounds.ts` plays
 * them, and `xiezi-square.ts` draws the rest. `xiezi-drill.ts` calls this at
 * each move.
 */

/**
 * What the feedback is built from, which is part of what the drill is built
 * from.
 */
export interface DrillFeedbackProperties {
  /**
   * The block's parts, whose square shows the stroke missed and flashes, and
   * whose pies hold the wedge a pass fills.
   */
  readonly parts: Pick<DrillParts, "square" | "pies">;

  /** What plays the notes and the character. Silence where absent. */
  readonly sounds?: DrillSounds | undefined;

  /** Whether finishing the character in the square finishes the whole drill. */
  readonly finishes?: (() => boolean) | undefined;
}

/** The feedback one drill gives. */
export class DrillFeedback {
  readonly #square: DrillParts["square"];
  readonly #pies: DrillParts["pies"];
  readonly #sounds: DrillSounds | undefined;
  readonly #finishes: () => boolean;

  constructor(properties: DrillFeedbackProperties) {
    this.#square = properties.parts.square;
    this.#pies = properties.parts.pies;
    this.#sounds = properties.sounds;
    this.#finishes = properties.finishes ?? ((): boolean => true);
  }

  /**
   * Ready the character that has come into the square to be said.
   * @param recordings - Its recordings, one per voice.
   */
  prepare(recordings: readonly string[]): void {
    this.#sounds?.prepare(recordings);
  }

  /** A stroke taken that did not finish the pass. */
  strokeTaken(): void {
    this.#sounds?.play("stroke");
  }

  /**
   * A stroke that was not the one wanted, which is written in for the reader.
   * @param wanted - The stroke that was wanted.
   */
  strokeMissed(wanted: number): void {
    this.#square.demonstrate(wanted);
    this.#sounds?.play("miss");
  }

  /** A character from memory marked wrong. */
  passMissed(): void {
    this.#sounds?.play("miss");
  }

  /**
   * A pass done, the character written whole.
   * @param before - Where the reader was.
   * @param after - Where the pass left them.
   */
  passDone(before: DrillProgress, after: DrillProgress): void {
    this.#square.flash(this.#pies?.wedge(before));
    this.#sounds?.play(passMoment(before, after, this.#finishes()), true);
  }
}
